import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ListSkeleton } from './SkeletonLoaders';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  FilterList,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { getAuditLogs, getAuditActionTypes } from '../api';

// Action type color mapping
const getActionColor = (action) => {
  if (action.includes('DELETE')) return 'error';
  if (action.includes('CREATE')) return 'success';
  if (action.includes('UPDATE') || action.includes('CHANGE')) return 'warning';
  if (action.includes('LOGIN_FAILED')) return 'error';
  if (action.includes('LOGIN')) return 'info';
  if (action.includes('LOCKOUT')) return 'warning';
  return 'default';
};

// Format action name for display
const formatAction = (action) => {
  return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

// Row component with expandable details
function AuditLogRow({ log, isMobile }) {
  const [open, setOpen] = useState(false);
  
  const hasDetails = log.old_values || log.new_values || log.details;

  // Mobile Card View for a single row
  if (isMobile) {
    return (
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          {/* Header with action and timestamp */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Chip
                label={formatAction(log.action)}
                color={getActionColor(log.action)}
                size="small"
                sx={{ mb: 0.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                {new Date(log.created_at).toLocaleString()}
              </Typography>
            </Box>
          </Box>

          {/* Details */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>User:</Typography>
              <Typography variant="caption" fontWeight="medium">{log.username || '-'}</Typography>
            </Box>
            {log.entity_type && (
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Entity:</Typography>
                <Typography variant="caption" fontWeight="medium">
                  {log.entity_type} {log.entity_id ? `#${log.entity_id}` : ''}
                </Typography>
              </Box>
            )}
            {log.ip_address && (
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>IP:</Typography>
                <Typography variant="caption" fontWeight="medium">{log.ip_address}</Typography>
              </Box>
            )}
          </Box>

          {/* Summary details if any */}
          {log.details && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {log.details}
            </Typography>
          )}

          {/* Expandable details */}
          {hasDetails && (log.old_values || log.new_values) && (
            <>
              <Button
                size="small"
                onClick={() => setOpen(!open)}
                endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                sx={{ mt: 1 }}
              >
                {open ? 'Hide Details' : 'View Details'}
              </Button>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 1 }}>
                  {log.old_values && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="error" fontWeight="bold">Previous Values:</Typography>
                      <Paper sx={{ p: 1, bgcolor: 'action.hover', mt: 0.5 }}>
                        <pre style={{ margin: 0, fontSize: '10px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.old_values, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                  {log.new_values && (
                    <Box>
                      <Typography variant="caption" color="success.main" fontWeight="bold">New Values:</Typography>
                      <Paper sx={{ p: 1, bgcolor: 'action.hover', mt: 0.5 }}>
                        <pre style={{ margin: 0, fontSize: '10px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.new_values, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Desktop Table Row
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {hasDetails && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          {new Date(log.created_at).toLocaleString()}
        </TableCell>
        <TableCell>
          <Chip
            label={formatAction(log.action)}
            color={getActionColor(log.action)}
            size="small"
          />
        </TableCell>
        <TableCell>{log.username || '-'}</TableCell>
        <TableCell>
          {log.entity_type ? (
            <Typography variant="body2">
              {log.entity_type} {log.entity_id ? `#${log.entity_id}` : ''}
            </Typography>
          ) : '-'}
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
            {log.details || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {log.ip_address || '-'}
          </Typography>
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Grid container spacing={2}>
                  {log.old_values && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="error">
                        Previous Values:
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: 'action.hover' }}>
                        <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                          {JSON.stringify(log.old_values, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                  {log.new_values && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="success.main">
                        New Values:
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: 'action.hover' }}>
                        <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                          {JSON.stringify(log.new_values, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                  {log.details && !log.old_values && !log.new_values && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Details:
                      </Typography>
                      <Typography variant="body2">{log.details}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AuditLog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionTypes, setActionTypes] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    startDate: '',
    endDate: '',
  });

  const loadActionTypes = async () => {
    try {
      const res = await getAuditActionTypes();
      setActionTypes(res.data);
    } catch (err) {
      console.error('Failed to load action types:', err);
    }
  };

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      
      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      
      const res = await getAuditLogs(params);
      setLogs(res.data.logs);
      setTotalCount(res.data.total);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    loadActionTypes();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0); // Reset to first page on filter change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const entityTypes = ['user', 'employee', 'time_entry', 'expense', 'expense_item_receipt', 'company', 'affiliate', 'email_settings'];

  // Mobile Card View
  const renderMobileView = () => (
    <Stack spacing={0}>
      {loading ? (
        <ListSkeleton count={5} />
      ) : logs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No audit logs found</Typography>
        </Box>
      ) : (
        logs.map((log) => (
          <AuditLogRow key={log.id} log={log} isMobile={true} />
        ))
      )}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 50 }} />
            <TableCell>Timestamp</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Entity</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>IP Address</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                <TableCell><Skeleton variant="text" width="50%" /></TableCell>
              </TableRow>
            ))
          ) : logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                No audit logs found
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <AuditLogRow key={log.id} log={log} isMobile={false} />
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </TableContainer>
  );

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={isMobile ? 'h6' : 'h5'}>Transaction Log</Typography>
        <IconButton onClick={loadLogs} disabled={loading}>
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Mobile Filter Toggle */}
      {isMobile && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FilterList />}
          endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ mb: 2 }}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      )}

      {/* Filters */}
      <Collapse in={!isMobile || showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  name="action"
                  value={filters.action}
                  onChange={handleFilterChange}
                  label="Action Type"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  {Object.keys(actionTypes).map(key => (
                    <MenuItem key={key} value={actionTypes[key]}>
                      {formatAction(key)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  name="entityType"
                  value={filters.entityType}
                  onChange={handleFilterChange}
                  label="Entity Type"
                >
                  <MenuItem value="">All Entities</MenuItem>
                  {entityTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                name="startDate"
                label="Start Date"
                value={filters.startDate}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                name="endDate"
                label="End Date"
                value={filters.endDate}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Table or Cards */}
      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Mobile Pagination */}
      {isMobile && (
        <Paper sx={{ mt: 2 }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Per page:"
          />
        </Paper>
      )}
    </Box>
  );
}
